import { useTranslation } from "react-i18next";
import { CustomerTag } from "../../../utils/moduleTags/customerTag";
import { ProjectTag } from "../../../utils/moduleTags/projectTag";
import { CustomerSystemTag } from "../../../utils/moduleTags/customerSystemTag";
import { ModuleTag } from "../../../utils/moduleTags/moduleTag";
import {
  ServiceAgreementIntervalById,
  ServiceAgreementNextServiceStatus,
} from "./utils";
import { dateToString } from "../../../Utils";
import { DeliveryAddressSection } from "../../customers/deliveryAddresses/addressSection";
import { ServiceAgreementSubs } from "./service-agreement-subs";
import { isCompleteDeliveryAddress } from "../../../../constants/countries";
import { PromangRichTextContent } from "../../../utils/PromangRichTextContent";

function InfoCard({ title, description, children }) {
  return (
    <section className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.06)]">
      <div className="border-b border-slate-100 bg-[#EAF3FD] px-5 py-4">
        <h3 className="text-[15px] font-extrabold text-slate-900">{title}</h3>
        {description ? (
          <p className="mt-0.5 text-sm text-slate-600">{description}</p>
        ) : null}
      </div>
      <div className="px-5 py-5">{children}</div>
    </section>
  );
}

const agreementLineTotal = (line) => {
  const total = Number(line.sales_price || 0) * Number(line.quantity || 0) * (1 - Number(line.discount || 0) / 100);
  return Math.round(total * 100) / 100;
};

export const ServiceAgreementInfo = (props) => {
  const { t } = useTranslation();
  const { item, onRefresh } = props;
  const agreementLines = (item?.orderlines || []).filter((line) => !line.group_row);
  const contacts = Array.isArray(item?.contacts) ? item.contacts : [];
  const customerContacts = contacts.filter((row) => row.party === "customer");
  const supplierContacts = contacts.filter((row) => row.party === "supplier");
  const addressOk = isCompleteDeliveryAddress(item?.delivery_address);

  return (
    <div className="space-y-5">
      <InfoCard
        title={t("service.agreements.info.overview")}
        description={t("service.agreements.info.overview_help")}
      >
        <div className="mb-4 flex flex-wrap gap-2">
          {item?.is_framework ? (
            <span className="rounded-full bg-[#1463C7] px-3 py-1 text-xs font-bold text-white">
              {t("service.agreements.badge.framework")}
            </span>
          ) : (
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
              {t("service.agreements.badge.service")}
            </span>
          )}
          {item?.vat_included ? (
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
              {t("service.agreements.info.vat_included")}
            </span>
          ) : (
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
              {t("service.agreements.info.vat_excluded")}
            </span>
          )}
        </div>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <CustomerTag customer={item?.customer} />
          </div>
          <div>
            <ProjectTag project={item?.project} folder={item?.folder} />
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-500">
              {t("service.agreements.info.type")}
            </dt>
            <dd className="mt-1 text-sm text-slate-900">
              {item?.type?.displayTitle}
            </dd>
          </div>
          <div>
            <CustomerSystemTag
              customer={item?.customer}
              selected={item?.system}
            />
          </div>
        </dl>
      </InfoCard>

      {!addressOk ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          {t("service.agreements.address.incomplete_banner")}
        </div>
      ) : null}

      {(customerContacts.length > 0 || supplierContacts.length > 0 || item?.contact?.id) && (
        <InfoCard title={t("service.agreements.info.contacts")}>
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                {t("service.agreements.contacts.party.supplier")}
              </p>
              <ul className="mt-2 space-y-2">
                {supplierContacts.length === 0 ? (
                  <li className="text-sm text-slate-400">—</li>
                ) : (
                  supplierContacts.map((row) => (
                    <li key={row.id || row.displayTitle} className="text-sm">
                      <span className="font-semibold text-slate-900">
                        {row.displayTitle}
                      </span>
                      {row.role ? (
                        <span className="text-slate-500"> — {row.role}</span>
                      ) : null}
                      {row.email ? (
                        <div className="text-slate-500">{row.email}</div>
                      ) : null}
                    </li>
                  ))
                )}
              </ul>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                {t("service.agreements.contacts.party.customer")}
              </p>
              <ul className="mt-2 space-y-2">
                {(customerContacts.length ? customerContacts : item?.contact ? [{ displayTitle: item.contact.displayTitle, email: item.contact.email, is_primary: true }] : []).map((row) => (
                  <li key={row.id || row.displayTitle} className="text-sm">
                    <span className="font-semibold text-slate-900">
                      {row.displayTitle}
                    </span>
                    {row.is_primary ? (
                      <span className="ml-2 rounded-full bg-[#EAF3FD] px-2 py-0.5 text-[10px] font-bold text-[#0B3A78]">
                        {t("service.agreements.contacts.primary")}
                      </span>
                    ) : null}
                    {row.role ? (
                      <span className="text-slate-500"> — {row.role}</span>
                    ) : null}
                    {row.email ? (
                      <div className="text-slate-500">{row.email}</div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </InfoCard>
      )}

      {item?.description && (
        <InfoCard title={t("service.agreements.info.description")}>
          <PromangRichTextContent
            html={item.description}
            className="text-slate-700"
          />
        </InfoCard>
      )}

      {item?.agreement_text && (
        <InfoCard title={t("service.agreements.info.agreement_text")}>
          <PromangRichTextContent
            html={item.agreement_text}
            className="text-slate-700"
          />
        </InfoCard>
      )}

      {item?.conditions && (
        <InfoCard title={t("service.agreements.form.conditions")}>
          <PromangRichTextContent
            html={item.conditions}
            className="text-slate-700"
          />
        </InfoCard>
      )}

      {(item?.offer_price || item?.hourly_rate || item?.callout_fee || item?.parts_markup_percent || item?.subcontractor_markup_percent || agreementLines.length > 0) && (
        <InfoCard title={t("service.agreements.form.section_prices")}>
          {agreementLines.length > 0 ? (
            <ul className="mb-4 divide-y divide-slate-100 rounded-2xl border border-slate-200">
              {agreementLines.map((line) => (
                <li
                  key={line.id || line.title}
                  className="flex items-start justify-between gap-3 px-4 py-3 text-sm"
                >
                  <div>
                    <p className="font-semibold text-slate-900">
                      {line.title || line.product?.displayTitle || line.description}
                    </p>
                    <p className="text-slate-500">
                      {t(line.item_type === "service" ? "service.agreements.lines.type_service" : "service.agreements.lines.type_product")}
                      {" · "}
                      {line.interval_label || "—"}
                      {line.include_in_jobs === false ? ` · ${t("service.agreements.lines.contract_only")}` : ""}
                    </p>
                    {item?.interval_mode === "per_line" ? (
                      <p className="text-xs text-slate-500">
                        {t("service.agreements.info.line_next")}: {dateToString(line.next_service_date)}
                        {" · "}
                        {t("service.agreements.info.line_last")}: {line.last_service_date ? dateToString(line.last_service_date) : "—"}
                        {line.in_active_report ? (
                          <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">
                            {t("service.agreements.info.line_in_active_report")}
                          </span>
                        ) : line.due_next_visit ? (
                          <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 font-semibold text-blue-800">
                            {t("service.agreements.info.line_next_visit")}
                          </span>
                        ) : null}
                      </p>
                    ) : null}
                  </div>
                  <p className="font-semibold text-slate-900">{agreementLineTotal(line)}</p>
                </li>
              ))}
            </ul>
          ) : null}
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {item?.offer_price ? (
              <ModuleTag label={t("service.agreements.form.offer_price")}>
                {item.offer_price}
              </ModuleTag>
            ) : null}
            {item?.hourly_rate ? (
              <ModuleTag label={t("service.agreements.form.hourly_rate")}>
                {item.hourly_rate}
              </ModuleTag>
            ) : null}
            {item?.callout_fee ? (
              <ModuleTag label={t("service.agreements.form.callout_fee")}>
                {item.callout_fee}
              </ModuleTag>
            ) : null}
            {item?.parts_markup_percent ? (
              <ModuleTag label={t("service.agreements.form.parts_markup")}>
                {item.parts_markup_percent} %
              </ModuleTag>
            ) : null}
            {item?.subcontractor_markup_percent ? (
              <ModuleTag label={t("service.agreements.form.subcontractor_markup")}>
                {item.subcontractor_markup_percent} %
              </ModuleTag>
            ) : null}
          </dl>
        </InfoCard>
      )}

      {(item?.discount_percent || item?.discount_on_parts || item?.discount_on_services || (item?.discount_lines || []).length > 0) && (
        <InfoCard title={t("service.agreements.info.discount")}>
          {(item?.discount_lines || []).length > 0 ? (
            <ul className="mb-4 divide-y divide-slate-100 rounded-2xl border border-slate-200">
              {item.discount_lines.map((line) => (
                <li
                  key={line.id || line.displayTitle}
                  className="flex items-start justify-between gap-3 px-4 py-3 text-sm"
                >
                  <p className="font-semibold text-slate-900">
                    {line.displayTitle || line.label}
                  </p>
                  <p className="font-semibold text-slate-900">
                    {line.discount_percent} %
                  </p>
                </li>
              ))}
            </ul>
          ) : null}
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {item?.discount_percent ? (
              <ModuleTag label={t("service.agreements.form.discount_percent")}>
                {item.discount_percent} %
              </ModuleTag>
            ) : null}
            {item?.discount_on_parts ? (
              <ModuleTag label={t("service.agreements.form.discount_parts")}>
                {item.discount_on_parts} %
              </ModuleTag>
            ) : null}
            {item?.discount_on_services ? (
              <ModuleTag label={t("service.agreements.form.discount_services")}>
                {item.discount_on_services} %
              </ModuleTag>
            ) : null}
          </dl>
        </InfoCard>
      )}

      <InfoCard title={t("service.agreements.info.interval")}>
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <ModuleTag label={t("service.agreements.info.interval")}>
            {ServiceAgreementIntervalById(item?.interval?.id)?.label || "—"}
            {item?.interval_mode === "per_line" ? ` · ${t("service.agreements.form.interval_mode_per_line")}` : ""}
          </ModuleTag>
          <ModuleTag label={t("service.agreements.info.next_date")}>
            {dateToString(item?.next_service_date)}
          </ModuleTag>
          <ModuleTag label={t("service.agreements.info.days_until")}>
            <ServiceAgreementNextServiceStatus serviceAgreement={item} />
          </ModuleTag>
          <ModuleTag label={t("service.agreements.info.last_service")}>
            {dateToString(item?.last_service_date)}
          </ModuleTag>
          {item?.duration_years ? (
            <ModuleTag label={t("service.agreements.form.duration_years")}>
              {item.duration_years}
            </ModuleTag>
          ) : null}
          {item?.notice_months ? (
            <ModuleTag label={t("service.agreements.form.notice_months")}>
              {item.notice_months}
            </ModuleTag>
          ) : null}
        </dl>
      </InfoCard>

      <DeliveryAddressSection address={item?.delivery_address} />

      <ServiceAgreementSubs agreement={item} onRefresh={onRefresh} />
    </div>
  );
};
